export const procurementEn = {
  originals: {
    history: 'Browse version history',
    search: 'Search originals',
    sort: 'Order',
    newest: 'Newest first',
    oldest: 'Oldest first',
    noResults: 'No original version matches the search. The current version remains available.',
    current: 'Show current version',
    xmlDownload: 'Download XML evidence',
    xmlChecksum: 'XML checksum',
    reviewedDownload: 'Download reviewed original',
    revision: 'Original version',
    version: 'Version {{number}}',
    previous:
      'You are viewing an earlier original. Approval uses the current version identified in the posting preview.',
    file: 'New original file',
    reason: 'Reason for replacement',
    save: 'Save original version',
    replace: 'Replace original',
    add: 'Add original',
    download: 'Download original',
    source: 'Source',
    recorded: 'Archived on',
    checksum: 'SHA-256 checksum',
    retained:
      'Earlier originals are retained. Replacing a file returns the invoice to review and invalidates existing approval previews.',
    electronicImmutable:
      'The imported electronic invoice original remains unchanged. A corrected electronic invoice must be reviewed and imported again.',
    legacy:
      'This original is in the previous storage system. Explicit replacement first archives the previous file as a separate version; the archive date does not claim a historical receipt date.',
    downloadOnly: 'Download this original securely as a file; this format has no embedded preview.',
    reasonRequired: 'Please explain the replacement (at least 3 characters).',
    reasonInvalid: 'The reason must contain no more than 500 characters.',
    fileRequired: 'Please choose an original file.',
    loadError: 'The original history could not be loaded.',
    invalidSource:
      'The original evidence is missing or its checksum or assignment is invalid. Please check the original file.',
    notEditable: 'The original of an approved or cancelled invoice cannot be replaced.',
    immutable: 'Original versions are retained unchanged.',
    privateSource: 'Incoming invoice originals are internal and cannot be linked publicly.',
    reviewed: 'Reviewed original',
    sources: {
      MANUAL_UPLOAD: 'Manual upload',
      LEGACY_CAPTURE: 'Retained from previous storage',
      ELECTRONIC_IMPORT: 'Electronic invoice import',
    },
  },
  orderStatus: {
    DRAFT: 'Draft',
    SENT: 'Sent',
    CONFIRMED: 'Confirmed',
    PARTIALLY_RECEIVED: 'Partially received',
    RECEIVED: 'Fully received',
    BILLED: 'Billed',
    CANCELLED: 'Cancelled',
  },
  receiptStatus: {
    DRAFT: 'Draft',
    POSTED: 'Posted',
    CANCELLED: 'Cancelled',
  },
  qualityStatus: {
    ACCEPTED: 'Accepted',
    DAMAGED_REJECTED: 'Damaged / rejected',
    UNDER_QUARANTINE: 'Under quarantine',
  },
  invoiceStatus: {
    DRAFT: 'Awaiting review',
    MATCHED: 'Matched',
    DISCREPANCY: 'Price discrepancy',
    APPROVED_FOR_PAYMENT: 'Approved for payment',
    PAID: 'Paid',
    CANCELLED: 'Cancelled',
  },
  matchStatus: {
    PERFECT_MATCH: 'Perfect match',
    TOLERANCE_ACCEPTED: 'Within tolerance',
    PRICE_MISMATCH: 'Price mismatch',
    QTY_MISMATCH: 'Quantity mismatch',
    MANUALLY_OVERRIDDEN: 'Manually accepted',
  },
  orders: {
    invalidNumber: 'Enter a valid number; quantity must be greater than 0.',
    invalidTax: 'Enter a tax rate between 0 and 100.',
    title: 'Purchase orders',
    subtitle: 'Create, send and track supplier orders.',
    cardTitle: 'Order overview',
    cardDescription: 'All purchase orders with delivery status and order value.',
    create: 'New purchase order',
    newReceipt: 'Record goods receipt',
    searchPlaceholder: 'Search order number or supplier',
    error: 'Purchase orders could not be loaded.',
    emptyTitle: 'No purchase orders found',
    emptyHint: 'Create a purchase order to start the procurement process.',
    columns: {
      number: 'Order number',
      supplier: 'Supplier',
      orderDate: 'Order date',
      delivery: 'Delivery date',
      status: 'Status',
      total: 'Order value',
      article: 'Article',
      quantity: 'Quantity',
      unit: 'Unit',
      price: 'Purchase price',
      tax: 'VAT %',
      lineNet: 'Net',
      freeText: 'Free text',
    },
    created: 'Purchase order {{number}} has been created.',
    saved: 'Purchase order saved.',
    loadError: 'The purchase order could not be loaded.',
    newTitle: 'New purchase order',
    editorHint: 'Items, terms and delivery address of the purchase order.',
    pdf: 'Download PDF',
    send: 'Send by email',
    confirm: 'Order confirmation received',
    confirmed: 'Purchase order marked as confirmed.',
    cancel: 'Cancel order',
    cancelled: 'Purchase order has been cancelled.',
    cancelTitle: 'Cancel purchase order {{number}}?',
    cancelDescription:
      'The purchase order will be cancelled permanently and can no longer be edited afterwards.',
    cancelReason: 'Cancellation reason',
    receiveGoods: 'Record goods receipt',
    itemsTitle: 'Items',
    itemsHint: 'Supplier articles with tier prices or free text items.',
    articlePlaceholder: 'Search article',
    noSupplierProducts: 'No articles are assigned to this supplier.',
    freeTextPlaceholder: 'Item description',
    removeItem: 'Remove item',
    addItem: 'Add item',
    net: 'Net',
    tax: 'VAT',
    gross: 'Gross',
    headerTitle: 'Order header',
    headerHint: 'Supplier, dates and terms.',
    supplier: 'Supplier',
    supplierPlaceholder: 'Search supplier',
    noSuppliers: 'No suppliers found.',
    minimumOrderValue: 'Minimum order value',
    deliveryTime: 'Lead time',
    days: '{{count}} days',
    expectedDelivery: 'Expected delivery date',
    paymentTerms: 'Payment terms (days)',
    incoterms: 'Incoterms',
    storageArea: 'Target storage area',
    storageAreaHint: 'Pre-filled when recording the goods receipt.',
    notes: 'Notes',
    email: {
      title: 'Send purchase order',
      description: 'The order is attached as PDF and the status is set to "Sent".',
      to: 'Recipient',
      subject: 'Subject',
      body: 'Message',
      submit: 'Send',
      sent: 'Purchase order has been sent.',
      defaultSubject: 'Purchase order {{number}}',
      defaultBody: 'Hello,\n\nplease find attached our purchase order {{number}}.\n\nKind regards',
    },
  },
  receipts: {
    warehouseRequired: 'Select the warehouse for this bin. A label alone does not identify stock.',
    title: 'Goods receipts',
    subtitle: 'Overview of posted and open goods receipts.',
    cardTitle: 'Goods receipt overview',
    cardDescription: 'All recorded deliveries with their purchase order reference.',
    create: 'Record goods receipt',
    searchPlaceholder: 'Receipt, order or delivery note number',
    error: 'Goods receipts could not be loaded.',
    listEmptyTitle: 'No goods receipts yet',
    listEmptyHint: 'Record a delivery to update your stock.',
    columns: {
      number: 'Receipt number',
      supplier: 'Supplier',
      order: 'Purchase order',
      deliveryNote: 'Delivery note',
      date: 'Received on',
      status: 'Status',
    },
    terminalTitle: 'Record goods receipt',
    terminalSubtitle: 'Scan the order, verify quantities and post stock bindingly.',
    selectOrder: 'Select purchase order',
    selectOrderHint: 'Scan the order number or pick one of the open purchase orders.',
    scanOrder: 'Scan order number',
    scanPlaceholder: 'e.g. EK-2026-0001',
    openOrders: 'Open purchase orders',
    openOrdersPlaceholder: 'Select order',
    emptyTitle: 'No purchase order selected',
    emptyHint: 'Scan the delivery note barcode or select a purchase order.',
    loadError: 'The order items could not be loaded.',
    positionsTitle: 'Items of {{number}}',
    nothingOpen: 'This purchase order has no open quantities.',
    batchManaged: 'Batch managed',
    open: 'Open: {{quantity}} {{unit}}',
    quantityReceived: 'Received quantity',
    quantityInvalid: 'Please enter a quantity greater than 0.',
    batchNumber: 'Batch number',
    batchRequired: 'A batch number is mandatory for batch managed articles.',
    expiryDate: 'Best before date',
    expiryRequired: 'Please enter a best before date.',
    storageLocation: 'Storage bin / shelf',
    storageLocationPlaceholder: 'e.g. A-03-12',
    quality: 'Quality',
    deliveryNoteTitle: 'Delivery note',
    deliveryNoteHint: 'Record the delivery note number and attach a scan or photo.',
    deliveryNoteNumber: 'Delivery note number',
    storageArea: 'Storage area',
    notes: 'Notes',
    attachScan: 'Scan or upload delivery note',
    noScan: 'No document attached',
    blocked: '{{count}} item(s) are still incomplete.',
    ready: '{{count}} item(s) will be posted.',
    submit: 'Post goods receipt & update stock',
    posted: 'Goods receipt {{number}} was posted and stock was updated.',
  },
  invoices: {
    metadataTitle: 'Edit accounting details: {{number}}',
    importedMetadataHint:
      'This electronic invoice retains its original amounts and lines. Edit receipt date, performance date, due date and accounting details here.',
    metadataSaved: 'Accounting details saved. The invoice requires a new review.',
    metadataSaveFailed: 'Accounting details could not be saved. Your input has been retained.',
    importedSourceLocked:
      'Imported invoice values are protected. Edit accounting details separately; a different original invoice requires a reviewed new import.',

    validation: {
      summary: 'Check the highlighted entries. Your input is retained.',
      supplier: 'Choose a supplier.',
      invoiceNumber: 'Enter an invoice number with 1 to 60 characters.',
      date: 'Enter a valid calendar date.',
      currency: 'Enter a valid three-letter currency code, for example EUR.',
      rate: 'Enter a positive exchange rate with at most 10 decimal places.',
      source: 'Enter traceable rate evidence with 3 to 240 characters.',
      amount: 'Enter a valid amount with at most 4 decimal places.',
      quantity: 'Enter a positive quantity with at most 6 decimal places.',
      percent: 'Enter a percentage from 0 to 100 with at most 6 decimal places.',
      days: 'Enter a permitted whole number of cash-discount days.',
      description: 'Enter a description with 1 to 200 characters.',
      decimalHint: 'Decimal separator: point or comma. Do not use thousands separators.',
    },
    editTitle: 'Edit incoming invoice',
    editSubmit: 'Save changes',
    withoutOrder: 'Without purchase order',
    optionalOrder: 'Optional. Selecting an order copies its items.',
    invoiceReceivedDate: 'Invoice received on',
    serviceDate: 'Service date',
    accountingDates: 'Posting dates and exchange rate',
    accountingDatesHint:
      'Book and tax dates are checked on approval. The book rate does not replace a legally required tax exchange rate.',
    captureLoadError: 'Invoice capture settings could not be loaded.',
    draftSaved: 'Draft {{number}} is saved. Retry uploading the original or open the draft.',
    retryUpload: 'Retry original upload',
    openDraft: 'Open draft',
    title: 'Vendor invoices',
    subtitle: 'Monitor creditors by due date and cash discount deadline.',
    cardTitle: 'Incoming invoices',
    cardDescription: 'All supplier invoices with review and payment status.',
    create: 'Record invoice',
    createTitle: 'Record vendor invoice',
    createDescription:
      'Capture an invoice with or without a purchase order and attach the original for review.',
    created: 'Invoice {{number}} has been recorded.',
    searchPlaceholder: 'Search invoice number or supplier',
    error: 'Vendor invoices could not be loaded.',
    emptyTitle: 'No vendor invoices found',
    emptyHint: 'Record a supplier invoice to start the review.',
    blocked: 'Payment blocked',
    order: 'Purchase order',
    orderPlaceholder: 'Select order',
    number: 'Invoice number',
    invoiceDate: 'Invoice date',
    discountPercent: 'Cash discount %',
    discountDays: 'Discount days',
    document: 'Invoice document (PDF or scan)',
    addItem: 'Add item',
    removeItem: 'Remove item',
    submit: 'Record invoice',
    discountHint: 'Cash discount {{percent}} % available for {{count}} more days',
    discountHint_one: 'Cash discount {{percent}} % available today only',
    discountGone: 'Discount period expired',
    kpi: {
      open: 'Open amount',
      overdue: 'Overdue',
      discrepancy: 'With discrepancy',
      discount: 'Discount expiring',
    },
    columns: {
      number: 'Invoice number',
      supplier: 'Supplier',
      invoiceDate: 'Invoice date',
      dueDate: 'Due on',
      discount: 'Cash discount',
      status: 'Status',
      gross: 'Gross',
      description: 'Description',
      quantity: 'Quantity',
      unitPrice: 'Unit price',
      taxRate: 'VAT %',
    },
  },
  match: {
    title: 'Three-way match {{number}}',
    loadError: 'The invoice match could not be loaded.',
    run: 'Recalculate match',
    clear: 'All items match.',
    discrepancies: '{{count}} item(s) deviate.',
    documentTitle: 'Supplier invoice',
    documentError: 'The document could not be loaded.',
    noDocument: 'No document attached',
    noDocumentHint: 'Upload the invoice as PDF or scan to review it here.',
    upload: 'Upload document',
    uploaded: 'Document has been stored.',
    zoomIn: 'Zoom in',
    zoomOut: 'Zoom out',
    rotate: 'Rotate',
    reload: 'Reload',
    tableTitle: 'Order / delivery / invoice',
    counts: '{{matched}} exact · {{tolerance}} within tolerance · {{discrepancy}} deviating',
    columns: {
      position: 'Item',
      description: 'Description',
      ordered: 'Ordered',
      delivered: 'Delivered',
      invoiced: 'Invoiced',
      variance: 'Variance',
      status: 'Status',
    },
    totalVariance: 'Total variance',
    approve: 'Approve for payment',
    approved: 'Invoice has been approved for payment.',
    acceptTolerance: 'Accept tolerance manually',
    override: {
      title: 'Accept variance manually',
      description: 'The reason is stored in the match log and the audit trail.',
      reason: 'Reason',
      submit: 'Accept variance',
      saved: 'Variance has been accepted.',
    },
    complaint: {
      action: 'Send complaint',
      title: 'Complaint to supplier',
      description: 'The message is sent by email and logged.',
      to: 'Email address',
      subject: 'Subject',
      body: 'Message',
      submit: 'Send complaint',
      sent: 'Complaint has been sent.',
      defaultSubject: 'Query regarding invoice {{number}}',
      defaultBody:
        'Hello,\n\nwhile reviewing your invoice {{number}} we found a variance of {{variance}} compared to the purchase order and the goods receipt.\n\nPlease review the case and send us a corrected invoice.\n\nKind regards',
    },
  },
} as const;
